import { Box, Heading, Text, VStack, HStack, Code, Badge, List } from '@chakra-ui/react';
import { CanvassingPageShell } from '../components/canvassing/CanvassingPageShell'
import { TurfMap, TurfDrawingMap } from '../components/turf'
import type { ContactMarker, TurfBoundary } from '../types/turfMap'
import * as GeoJSON from 'geojson'
import { useIsMobile } from '../hooks/useIsMobile'
import { DesktopOnlyNotice } from '../components/shared'

// Test data - Washington, DC area
const testBoundary: TurfBoundary = {
  id: 'test-turf-1',
  label: 'Test Turf',
  geoJson: {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [-77.05, 38.90],
          [-77.05, 38.92],
          [-77.03, 38.92],
          [-77.03, 38.90],
          [-77.05, 38.90],
        ],
      ],
    },
    properties: {},
  },
}

const testContacts: ContactMarker[] = [
  { id: '1', longitude: -77.0428, latitude: 38.9072, label: 'Contact 1', walkOrder: 1, isVisited: false },
  { id: '2', longitude: -77.0369, latitude: 38.9072, label: 'Contact 2', walkOrder: 2, isVisited: true },
  { id: '3', longitude: -77.0369, latitude: 38.9007, label: 'Contact 3', walkOrder: 3, isVisited: false },
]

const testRoute: GeoJSON.Feature<GeoJSON.LineString> = {
  type: 'Feature',
  geometry: {
    type: 'LineString',
    coordinates: [
      [-77.0428, 38.9072],
      [-77.0369, 38.9072],
      [-77.0369, 38.9007],
    ],
  },
  properties: {},
}

export function CanvassingTurfCutterPage() {
  const isMobile = useIsMobile()

  const handlePolygonComplete = (polygon: GeoJSON.Feature<GeoJSON.Polygon>) => {
    console.log('Polygon drawn:', polygon)
  }

  if (isMobile) {
    return (
      <CanvassingPageShell
        title="Turf Cutter"
        subtitle="Draw geographic boundaries on a map to create turfs"
      >
        <DesktopOnlyNotice
          title="The Turf Cutter is coming to mobile"
          message="Drawing and cutting turf isn't available on phones yet."
          desktopHint="Open this on a desktop browser to cut turf."
        />
      </CanvassingPageShell>
    )
  }

  return (
    <CanvassingPageShell
      title="Turf Cutter"
      subtitle="Draw geographic boundaries on a map to create turfs"
    >
      <VStack gap={6} align="stretch">
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <Heading size="md" mb={4}>
            What is Turf Cutting?
          </Heading>
          <Text color="gray.700" mb={4}>
            Turf cutting is the process of dividing a contact list into geographic regions (turfs)
            for canvassing assignments. Each turf contains a subset of contacts and is assigned to
            one or more canvassers.
          </Text>

          <Heading size="sm" mt={6} mb={3}>
            How Turfs Work
          </Heading>
          <VStack gap={3} align="stretch">
            <Box p={4} bg="info.subtle" borderRadius="md" borderLeft="4px solid" borderColor="info.fg">
              <Text fontWeight="semibold" mb={1}>
                DRAW BOUNDARIES
              </Text>
              <Text fontSize="sm" color="gray.700">
                Use a map interface to draw polygon boundaries around neighborhoods or areas.
                Boundaries are stored using PostGIS geography data types.
              </Text>
            </Box>

            <Box p={4} bg="green.50" borderRadius="md" borderLeft="4px solid" borderColor="green.400">
              <Text fontWeight="semibold" mb={1}>
                AUTO-ASSIGN CONTACTS
              </Text>
              <Text fontSize="sm" color="gray.700">
                Contacts from the selected list are automatically assigned to turfs based on their
                address geocoordinates falling within turf boundaries.
              </Text>
            </Box>

            <Box p={4} bg="purple.50" borderRadius="md" borderLeft="4px solid" borderColor="purple.400">
              <Text fontWeight="semibold" mb={1}>
                ASSIGN CANVASSERS
              </Text>
              <Text fontSize="sm" color="gray.700">
                Assign one or more canvassers to each turf. Canvassers only see contacts within
                their assigned turfs during field work.
              </Text>
            </Box>

            <Box p={4} bg="orange.50" borderRadius="md" borderLeft="4px solid" borderColor="orange.400">
              <Text fontWeight="semibold" mb={1}>
                TRACK PROGRESS
              </Text>
              <Text fontSize="sm" color="gray.700">
                Monitor completion rates per turf: total contacts, contacted, remaining, success
                rate.
              </Text>
            </Box>
          </VStack>

          <Heading size="sm" mt={6} mb={3}>
            Turf vs Territory
          </Heading>
          <Text color="gray.700" mb={3}>
            The system distinguishes between two geographic concepts:
          </Text>
          <List.Root as='ul' gap={2} pl={4}>
            <List.Item>
              <strong>Territories:</strong> Persistent official boundaries (city council districts,
              state legislative districts, precincts). Imported from GIS files or defined once.
            </List.Item>
            <List.Item>
              <strong>Turfs:</strong> Temporary canvassing zones created for specific campaigns or
              time periods. Tied to a contact list and used for field assignments.
            </List.Item>
          </List.Root>

          <Heading size="sm" mt={6} mb={3}>
            Turf Creation Workflow
          </Heading>
          <List.Root as='ul' gap={2} pl={4}>
            <List.Item>Select a contact list (all contacts must have geocoded addresses)</List.Item>
            <List.Item>Use the map interface to draw turf boundaries (polygon drawing tools)</List.Item>
            <List.Item>
              Name each turf and optionally set a target contact count or completion date
            </List.Item>
            <List.Item>Assign canvassers to turfs (multiple canvassers per turf supported)</List.Item>
            <List.Item>
              Contacts are automatically filtered based on their location within turf boundaries
            </List.Item>
            <List.Item>Export walking/driving routes for each turf (future feature)</List.Item>
          </List.Root>

          <Heading size="sm" mt={6} mb={3}>
            PostGIS Integration
          </Heading>
          <Text color="gray.700" mb={2}>
            Turfs use PostgreSQL's PostGIS extension for spatial operations:
          </Text>
          <List.Root as='ul' gap={1} pl={4} fontSize="sm">
            <List.Item>
              Boundaries stored as <Code fontSize="sm">Geography(Polygon, 4326)</Code>
            </List.Item>
            <List.Item>
              Spatial queries use <Code fontSize="sm">ST_Contains()</Code> for point-in-polygon
              tests
            </List.Item>
            <List.Item>
              Contact addresses geocoded to <Code fontSize="sm">Geography(Point, 4326)</Code>
            </List.Item>
          </List.Root>

          <Heading size="sm" mt={6} mb={3}>
            Backend Models
          </Heading>
          <List.Root as='ul' gap={1} pl={4} fontSize="sm">
            <List.Item>
              <Code fontSize="sm">Turfs</Code> - Turf entity with boundary, name, list link
            </List.Item>
            <List.Item>
              <Code fontSize="sm">TurfAssignments</Code> - Junction table linking turfs to users
            </List.Item>
            <List.Item>
              <Code fontSize="sm">Territories</Code> - Persistent geographic boundaries for official
              districts
            </List.Item>
          </List.Root>
        </Box>

        <Box bg="green.50" p={6} borderRadius="lg" borderLeft="4px solid" borderColor="green.400">
          <HStack gap={2} mb={3}>
            <Badge colorPalette="green" fontSize="sm">
              PHASE 5 COMPLETE
            </Badge>
            <Heading size="sm">Frontend Map Infrastructure</Heading>
          </HStack>
          <Text color="gray.700" mb={3}>
            Map components are ready for use in Phase 6:
          </Text>
          <List.Root as='ul' gap={2} pl={4} color="gray.700">
            <List.Item>✅ MapLibre GL JS + react-map-gl integration</List.Item>
            <List.Item>✅ Protomaps vector tiles (CDN mode, swappable to self-hosted)</List.Item>
            <List.Item>✅ TurfMap component with boundaries, markers, clustering, and routes</List.Item>
            <List.Item>✅ TurfDrawingMap component with terra-draw polygon drawing</List.Item>
            <List.Item>✅ MapLoadingFallback component</List.Item>
            <List.Item>✅ TypeScript types for map components</List.Item>
          </List.Root>
        </Box>

        {/* Map Component Tests */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <Heading size="md" mb={4}>
            Map Component Tests
          </Heading>
          <VStack gap={6} align="stretch">
            {/* Empty Map */}
            <Box>
              <Heading size="sm" mb={3}>
                1. Empty Map (Basemap Only)
              </Heading>
              <TurfMap height="400px" fitToBounds={false} />
            </Box>

            {/* Map with Data */}
            <Box>
              <Heading size="sm" mb={3}>
                2. Map with Boundary, Contacts, and Route
              </Heading>
              <TurfMap
                height="400px"
                boundaries={[testBoundary]}
                contacts={testContacts}
                routeGeoJson={testRoute}
                onMarkerClick={(id) => console.log('Clicked contact:', id)}
                onBoundaryClick={(id) => console.log('Clicked boundary:', id)}
              />
            </Box>

            {/* Drawing Map */}
            <Box>
              <Heading size="sm" mb={3}>
                3. Drawing Map (Draw a Polygon)
              </Heading>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Click "Draw Polygon" to start drawing. Click to add vertices, double-click to complete.
                Check the browser console to see the drawn polygon GeoJSON.
              </Text>
              <TurfDrawingMap
                height="400px"
                fitToBounds={false}
                onPolygonComplete={handlePolygonComplete}
              />
            </Box>
          </VStack>
        </Box>
      </VStack>
    </CanvassingPageShell>
  );
}
